import React, { useMemo } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import Alert from '@/components/core/Alert';
import CheckBox from '@/components/core/Checkbox';
import { IconWarningTriangle } from '@/icons';
import {
  HandoffBlocker,
  summarizeHandoff,
} from '@/helpers/road_authority_helpers';

const messages = defineMessages({
  label: {
    id: 'road_authority_handoff.label',
    defaultMessage: 'Send to the road authority',
  },
  description: {
    id: 'road_authority_handoff.description',
    defaultMessage:
      "The work order goes to the road authority's maintenance district for the selected stops instead of a contractor.",
  },
  districtsHeading: {
    id: 'road_authority_handoff.districts_heading',
    defaultMessage: 'Maintenance district per stop',
  },
  missingDistrict: {
    id: 'road_authority_handoff.missing_district',
    defaultMessage: 'No maintenance district',
  },
  noEntities: {
    id: 'road_authority_handoff.no_entities',
    defaultMessage: 'Select at least one stop to send to the road authority.',
  },
  missingDistrictBlocker: {
    id: 'road_authority_handoff.missing_district_blocker',
    defaultMessage:
      '{count, plural, one {# selected stop has no maintenance district and cannot be sent to the road authority. Remove it or untick the choice.} other {# selected stops have no maintenance district and cannot be sent to the road authority. Remove them or untick the choice.}}',
  },
  unknownEntities: {
    id: 'road_authority_handoff.unknown_entities',
    defaultMessage:
      '{count, plural, one {# selected stop is not in the stop list, so its maintenance district cannot be checked. Reload the page and try again.} other {# selected stops are not in the stop list, so their maintenance district cannot be checked. Reload the page and try again.}}',
  },
  unmatched: {
    id: 'road_authority_handoff.unmatched',
    defaultMessage:
      'The road authority has no recipient for {names}. Nothing was created.',
  },
  unmatchedCount: {
    id: 'road_authority_handoff.unmatched_count',
    defaultMessage:
      'The road authority has no recipient for {count} of the selected stops. Nothing was created.',
  },
});

interface RoadAuthorityHandoffProps {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  /** The stops currently selected in the dialog, in selection order. */
  selectedEntities: Entity[];
  /** Selected ids the stop list does not know; they block the handoff. */
  unknownEntityCount?: number;
  /** Ids the API refused with 422 (no road-authority filterset matched). */
  unmatchedEntityIds?: string[];
  disabled?: boolean;
}

/**
 * The "Skicka till väghållaren" choice in the create work order dialog
 * (HK-1117). When on, the order goes to the road owner's contractors and the
 * API picks the maintenance district from each stop. Shows the district per
 * selected stop so the user can see where the order will land, and says why
 * submit is blocked when it cannot be sent yet.
 */
const RoadAuthorityHandoff: React.FC<RoadAuthorityHandoffProps> = ({
  checked,
  onCheckedChange,
  selectedEntities,
  unknownEntityCount = 0,
  unmatchedEntityIds = [],
  disabled,
}) => {
  const intl = useIntl();
  const summary = useMemo(
    () => summarizeHandoff(selectedEntities, unknownEntityCount),
    [selectedEntities, unknownEntityCount],
  );
  const unmatchedNames = useMemo(
    () =>
      selectedEntities
        .filter((entity) => unmatchedEntityIds.includes(entity.id))
        .map((entity) => entity.full_name),
    [selectedEntities, unmatchedEntityIds],
  );

  return (
    <div className="flex flex-col gap-3">
      <CheckBox
        id="edit-workorder-send-to-road-authority"
        checked={checked}
        disabled={disabled}
        onCheckedChange={(value) => onCheckedChange(value === true)}
        label={intl.formatMessage(messages.label)}
        description={intl.formatMessage(messages.description)}
      />
      {checked && (
        <div className="rounded-md border border-background-300 bg-background-100 p-3 text-sm">
          <p className="mb-1 text-[10px] uppercase leading-4 tracking-[1.5px] text-text-700">
            {intl.formatMessage(messages.districtsHeading)}
          </p>
          {summary.rows.length > 0 && (
            <ul
              data-testid="road-authority-districts"
              className="divide-y divide-background-300"
            >
              {summary.rows.map(({ entity, district }) => (
                <li
                  key={entity.id}
                  className="flex items-center justify-between gap-2 py-1"
                >
                  <span className="text-text-900">{entity.full_name}</span>
                  {district ? (
                    <span className="shrink-0 text-text-700">{district}</span>
                  ) : (
                    <span className="inline-flex shrink-0 items-center gap-1 text-red-700">
                      <IconWarningTriangle aria-hidden className="size-4" />
                      {intl.formatMessage(messages.missingDistrict)}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
          {summary.blocker && (
            <p role="status" className="mt-2 text-red-700">
              <BlockerMessage blocker={summary.blocker} />
            </p>
          )}
          {unmatchedEntityIds.length > 0 && (
            <Alert severity="error" className="mt-3">
              {unmatchedNames.length === unmatchedEntityIds.length ? (
                <FormattedMessage
                  {...messages.unmatched}
                  values={{
                    names: intl.formatList(unmatchedNames, {
                      type: 'conjunction',
                    }),
                  }}
                />
              ) : (
                // A refused id we cannot name (not among the selected stops
                // any more): still say that nothing was created.
                <FormattedMessage
                  {...messages.unmatchedCount}
                  values={{ count: unmatchedEntityIds.length }}
                />
              )}
            </Alert>
          )}
        </div>
      )}
    </div>
  );
};

const BlockerMessage: React.FC<{ blocker: HandoffBlocker }> = ({ blocker }) => {
  switch (blocker.reason) {
    case 'no_entities':
      return <FormattedMessage {...messages.noEntities} />;
    case 'missing_district':
      return (
        <FormattedMessage
          {...messages.missingDistrictBlocker}
          values={{ count: blocker.entities.length }}
        />
      );
    case 'unknown_entities':
      return (
        <FormattedMessage
          {...messages.unknownEntities}
          values={{ count: blocker.count }}
        />
      );
    default: {
      const exhaustive: never = blocker;
      return exhaustive;
    }
  }
};

export default RoadAuthorityHandoff;
